import React from 'react';

// Year-over-year pipeline funnel: one section per stage, one horizontal bar
// per cohort within it. A bar's length is its count relative to the largest
// count anywhere in the chart, so stages are visually comparable to each
// other and not just within a stage.
//
// A null count INSIDE a group the backend emitted means that group's stage
// counts are indeterminate (`known: false` on the group -- see
// CoverageNotice for why). It renders as "No data", never as an empty bar:
// an empty bar reads as zero, and zero and unknown are different numbers
// that must never be conflated.
//
// A group MISSING from a cohort is the opposite case and must not be
// confused with it. `build_funnel` only emits a group once at least one
// record lands in it, so a category with thirty tasks last year and none
// this year is simply absent from this year's cohort -- which is a certain
// 0, not a mystery. Rendering that as "No data" would break the same rule
// from the other side, on exactly the had-work-last-year-none-this-year
// comparison this page exists to make.
//
// Colors: the dataviz skill's validated categorical palette, first three
// slots (blue / orange / aqua) -- these are the slots that clear the
// all-pairs CVD floor in both light and dark mode, which is the check that
// applies here because every cohort bar within a stage section sits beside
// every other. `default_cohorts()` on the backend caps at 3 fiscal years for
// the same reason a 4th color slot would: past three, the chart should facet
// or fold, not add a color that can't be told apart from its neighbours. A
// cohort beyond the third gets a neutral, uncolored bar rather than a reused
// or ambiguous hue -- its FY label and value are still direct-labeled, so no
// information is lost, only the color-identity channel.
const COHORT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a'];
// The dataviz palette's dark-mode secondary-ink token, reused here as a
// light-mode-only fallback fill -- effectively unreachable since the
// backend's default_cohorts() caps at 3, and even if it were reached it is
// a decorative fill always paired with a direct-labeled FY and value.
const NEUTRAL_COLOR = '#c3c2b7';

const INK_PRIMARY = '#0b0b0b';
const INK_SECONDARY = '#52514e';
const INK_MUTED = '#898781';
const GRIDLINE = '#e1e0d9';

const colorFor = (index) => COHORT_COLORS[index] ?? NEUTRAL_COLOR;

const groupIn = (cohort, groupKey) =>
  (cohort.groups || []).find((g) => g.key === groupKey);

// The one place that decides whether a number is real:
//   - a group ABSENT from a cohort is a known 0. `build_funnel` emits a
//     group only once a record lands in it, so absence means none did.
//   - a null INSIDE a present group is indeterminate. The backend nulls
//     exactly the intermediate stages of a group whose own `known` is
//     false, so that null IS `known: false` expressed at stage
//     granularity -- and it leaves the terminal count alone, because
//     completed_date is independent of the audit trail.
// What is never consulted here is the cohort's `stage_counts_available`:
// it is the OR of its groups, so it reads true whenever ANY group's
// numbers are real and says nothing about the group being drawn.
const stageValue = (cohort, groupKey, index) => {
  const group = groupIn(cohort, groupKey);
  if (!group) return { value: 0, unknown: false };
  const raw = group.stages ? group.stages[index] : undefined;
  if (raw === null || raw === undefined) return { value: null, unknown: true };
  return { value: raw, unknown: false };
};

export default function FunnelChart({ funnel, groupKey }) {
  const { stages, cohorts } = funnel;

  const counts = cohorts.flatMap((c) => {
    const g = groupIn(c, groupKey);
    return g ? g.stages.filter((n) => n !== null && n !== undefined) : [];
  });
  // Guard against both the all-null case (no counts at all) and the
  // all-zero case (every known count is a real 0) -- either way max must
  // never be 0, or a real 0 count computes (0 / 0) * 100 = NaN and emits an
  // invalid `width: NaN%` that browsers silently swallow.
  const max = counts.length ? Math.max(...counts, 1) : 1;

  const terminal = stages.length - 1;
  const current = cohorts[0];
  const prior = cohorts[1];
  // "None this year" is the headline the page exists for, so a group the
  // current cohort never emitted compares as the 0 it is rather than
  // silencing the sentence.
  const currentDone = current
    ? stageValue(current, groupKey, terminal)
    : { value: null, unknown: true };
  const priorDone = prior
    ? stageValue(prior, groupKey, terminal)
    : { value: null, unknown: true };
  const delta =
    !currentDone.unknown && !priorDone.unknown
      ? currentDone.value - priorDone.value
      : null;

  // Data identity, not a byproduct of the comparison sentence: taken from
  // any cohort carrying the key, so a group that exists in only one of them
  // -- a new employee, a category that stopped -- is still named.
  const label =
    (cohorts.map((c) => groupIn(c, groupKey)).find(Boolean) || {}).label ||
    groupKey;

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      {/* The section's own heading. The delta sentence below is a
          comparison and renders only when there is one to make; the label
          identifies the bars regardless, so it can never depend on it. */}
      <h3 style={{ fontSize: 15, color: INK_PRIMARY, margin: '0 0 6px' }}>
        {label}
      </h3>

      {delta !== null ? (
        <p style={{ fontSize: 14, color: INK_SECONDARY, margin: '0 0 16px' }}>
          {currentDone.value} complete vs{' '}
          {priorDone.value} at this point last year ({delta >= 0 ? '+' : ''}
          {delta})
        </p>
      ) : null}

      {/* Legend: a colored swatch beside its label, never color alone -- the
          dependable identity channel for two or more series. */}
      {cohorts.length > 1 ? (
        <div
          style={{
            display: 'flex',
            gap: 16,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          {cohorts.map((cohort, ci) => (
            <div
              key={cohort.fiscal_year}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: colorFor(ci),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12, color: INK_SECONDARY }}>
                FY{cohort.fiscal_year}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {stages.map((stage) => (
        <div
          key={stage.index}
          style={{
            marginBottom: 16,
            paddingBottom: 8,
            borderBottom: `1px solid ${GRIDLINE}`,
          }}
        >
          <div style={{ fontSize: 13, color: INK_SECONDARY, marginBottom: 6 }}>
            {stage.name}
          </div>
          {cohorts.map((cohort, ci) => {
            const { value, unknown } = stageValue(cohort, groupKey, stage.index);
            // The 2% floor keeps a small-but-real count visible. A genuine
            // 0 is exempt from it: ink on the page reads as "some", and a
            // stub bar beside the label "0" contradicts its own number.
            const width =
              unknown || value === 0
                ? 0
                : Math.max(Math.round((value / max) * 100), 2);
            const color = colorFor(ci);

            // The fiscal-year label lives outside the testid'd node on
            // purpose: a year like 2024 or 2025 contains the digit "0",
            // which would collide with an assertion that a null stage's bar
            // must not contain "0" anywhere in its text. Keeping the bar's
            // value display isolated from the row's other text is also just
            // the more honest scope for that testid -- it identifies "the
            // count for this cohort at this stage," not the row around it.
            //
            // The testid carries `groupKey` too: ReviewPage renders one
            // FunnelChart per group, so `bar-{fiscal_year}-{stage.index}`
            // alone would collide across group sections in the same DOM the
            // moment a cohort has more than one group.
            return (
              <div
                key={cohort.fiscal_year}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    width: 56,
                    fontSize: 12,
                    color: INK_MUTED,
                    flexShrink: 0,
                  }}
                >
                  FY{cohort.fiscal_year}
                </span>
                <div
                  data-testid={`bar-${groupKey}-${cohort.fiscal_year}-${stage.index}`}
                  title={
                    unknown
                      ? `FY${cohort.fiscal_year} ${stage.name}: no data (stage counts unavailable)`
                      : `FY${cohort.fiscal_year} ${stage.name}: ${value}`
                  }
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'block',
                      height: 14,
                      width: `${width}%`,
                      minWidth: width === 0 ? 0 : 3,
                      background: unknown ? 'transparent' : color,
                      // 4px rounded data-end at the tip, square at the baseline.
                      borderRadius: '0 4px 4px 0',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: unknown ? INK_MUTED : INK_PRIMARY,
                      fontStyle: unknown ? 'italic' : 'normal',
                    }}
                  >
                    {unknown ? 'No data' : value}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
